import React, { useEffect, useState } from 'react';
import TextWinds from '../common/TextWinds';
import CheckBoxes from '../common/CheckBoxes';
import { Terms } from '../../constants/types';
import { closeModal, openModal } from '../../store/features/modalSlice';
import Buttons from '../button/Buttons';
import { useAppDispatch } from '../../store/hook';
import HFbutton from '../button/HFbutton';
import { setCheckedTermsList } from '../../store/features/purchaseSlice';

interface TermDetailModalProps {
  term: Terms;
  handleCheck: (id: string, checked: boolean) => void;
}

/** 약관 자세히 보기 모달 - INFO: 약관에 content 가 없으면 노출 안 함 */
const TermDetailModal = ({ term, handleCheck }: TermDetailModalProps) => {
  const dispatch = useAppDispatch();
  const agreeHandler = (term: Terms) => () => {
    handleCheck(term.id, true);
    dispatch(closeModal());
  };
  const closeHandler = () => {
    dispatch(closeModal());
  };
  return (
    <div className="mountAnimation flex h-auto max-h-[80vh] w-[80vh] flex-col gap-4 rounded bg-white p-5">
      <TextWinds type="title_h3">{term.title}</TextWinds>
      <div className="h-full overflow-y-auto">
        <pre className="whitespace-pre-wrap">{term.content}</pre>
      </div>

      <div className="flex gap-3">
        <HFbutton onClick={agreeHandler(term)}>동의</HFbutton>
        <HFbutton type="primary-outlined" onClick={closeHandler}>
          닫기
        </HFbutton>
      </div>
    </div>
  );
};

interface TermsAgreementProps {
  /** 추가 약관 목록 */
  termsList: Terms[];
  onAllRequiredChecked: (allChecked: boolean) => void;
}

/**
 * 결제 페이지 - 약관 동의 컴포넌트
 * @description template에서 제공하는 약관 목록을 출력하고 (필수) 항목들의 전체 선택 여부를 판단
 * @param termsList 약관 목록
 * @param onAllRequiredChecked 모든 약관 동의 여부 변경 핸들러 (결제 가능 여부 판단)
 */
const TermsAgreement = ({
  termsList,
  onAllRequiredChecked,
}: TermsAgreementProps) => {
  const dispatch = useAppDispatch();

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const checkedTermsList = (termsList ?? []).filter((term) =>
      checkedIds.has(term.id),
    );
    dispatch(setCheckedTermsList(checkedTermsList));
  }, [checkedIds]);

  /** termsList 로딩 후 상태 초기화 (참조가 아닌 ID 기준으로 비교) */
  const termsKey = termsList?.map((t) => t.id).join(',') ?? '';
  useEffect(() => {
    if (termsKey) {
      setCheckedIds(new Set());
      onAllRequiredChecked(false);
    } else {
      setCheckedIds(new Set());
      onAllRequiredChecked(true);
    }
  }, [termsKey]);

  /** 모든 약관 동의 여부 판단 핸들러 */
  const emitAllRequiredStatus = (ids: Set<string> = checkedIds) => {
    const requiredTerms = (termsList ?? []).filter((t) => t.isRequired);
    const allApiRequired =
      requiredTerms.length === 0 || requiredTerms.every((t) => ids.has(t.id));

    onAllRequiredChecked(allApiRequired);
  };

  /** 약관 동의 - 항목별 동의 체크 핸들러 */
  const handleCheck = (id: string, checked: boolean) => {
    const next = new Set(checkedIds);
    checked ? next.add(id) : next.delete(id);
    setCheckedIds(next);
    emitAllRequiredStatus(next);
  };

  /** 카테고리별 그룹핑 처리 */
  const grouped = (termsList ?? []).reduce(
    (acc, term) => {
      if (!term.category?.id) return acc;
      const key = term.category.id;
      if (!acc[key]) acc[key] = { category: term.category, terms: [] };
      acc[key].terms.push(term);
      return acc;
    },
    {} as Record<
      string,
      {
        category: { id: string; name: string; sortOrder: number };
        terms: Terms[];
      }
    >,
  );

  return (
    <>
      {/* 약관 동의 */}
      {Object.values(grouped)
        .sort((a, b) => a.category.sortOrder - b.category.sortOrder)
        .map(({ category, terms }) => (
          <div
            key={category.id}
            className="mt-4 flex flex-col gap-4 rounded-md bg-white p-6"
          >
            <TextWinds type="title_h4">{category.name} 약관 동의</TextWinds>
            {terms
              .sort((a, b) => Number(b.isRequired) - Number(a.isRequired))
              .map((term) => (
                <div
                  className="flex flex-row items-center gap-[8px]"
                  key={term.id}
                >
                  <CheckBoxes
                    type="rounded"
                    color="purple5"
                    labelValue={`(${term.isRequired ? '필수' : '선택'}) ${term.title}`}
                    checked={checkedIds.has(term.id)}
                    onChange={(e) => handleCheck(term.id, e.target.checked)}
                  />
                  {term.content && (
                    <div>
                      <Buttons
                        type="text"
                        color="purple5"
                        className="min-w-[70px]"
                        onClick={() =>
                          dispatch(
                            openModal({
                              body: (
                                <TermDetailModal
                                  term={term}
                                  handleCheck={handleCheck}
                                />
                              ),
                            }),
                          )
                        }
                      >
                        자세히 보기
                      </Buttons>
                    </div>
                  )}
                </div>
              ))}
          </div>
        ))}
    </>
  );
};

export default TermsAgreement;
